<template>
  <div class="senders-view">
    <div class="header">
      <h2>Управление договорами с МНОС</h2>
      <el-button type="primary" @click="showCreateDialog = true">
        <el-icon><Plus /></el-icon>
        Добавить отправителя
      </el-button>
    </div>

    <el-table
      v-loading="loading"
      :data="senders"
      style="width: 100%"
      border
    >
      <el-table-column prop="name" label="Наименование" min-width="200" />
      <el-table-column prop="type" label="Тип" width="180">
        <template #default="{ row }">
          {{ senderTypeLabels[row.type as SenderTypeEnum] }}
        </template>
      </el-table-column>
      <el-table-column prop="inn" label="ИНН" width="150" />
      <el-table-column prop="bank_name" label="Банк" min-width="200" />
      <el-table-column prop="bank_bik" label="БИК" width="120" />
      <el-table-column label="Действия" width="150" fixed="right">
        <template #default="{ row }">
          <el-button-group>
            <el-button
              type="primary"
              circle
              size="large"
              @click="handleEdit(row)"
            >
              <el-icon><Edit /></el-icon>
            </el-button>
            <el-button
              type="primary"
              circle
              size="large"
              @click="handleDownload(row)"
            >
              <el-icon><Download /></el-icon>
            </el-button>
            <el-button
              type="danger"
              circle
              size="large"
              @click="handleDelete(row)"
            >
              <el-icon><Delete /></el-icon>
            </el-button>
          </el-button-group>
        </template>
      </el-table-column>
    </el-table>

    <!-- Create/Edit Dialog -->
    <el-dialog
      v-model="showCreateDialog"
      :title="editingSender ? 'Редактировать отправителя' : 'Новый отправитель'"
      width="80%"
      top="4vh"
      :close-on-click-modal="false"
    >
      <sender-information
        v-if="showCreateDialog"
        :is-standalone="true"
        :initial-sender="editingSender"
        @save="handleSave"
        @cancel="showCreateDialog = false"
      />
    </el-dialog>

    <!-- Delete Confirmation Dialog -->
    <el-dialog
      v-model="showDeleteDialog"
      title="Подтверждение удаления"
      width="30%"
      :close-on-click-modal="false"
    >
      <div class="delete-confirmation">
        <el-icon class="warning-icon" color="#F56C6C"><Warning /></el-icon>
        <p>Вы уверены, что хотите удалить отправителя "{{ senderToDelete?.name }}"?</p>
        <p class="warning-text">Это действие нельзя будет отменить.</p>
      </div>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="showDeleteDialog = false">Отмена</el-button>
          <el-button type="danger" @click="confirmDelete" :loading="loading">
            Удалить
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Edit, Delete, Download, Warning } from '@element-plus/icons-vue'
import type { Sender } from '@/store/types'
import { SenderTypeEnum } from '@/store/types'
import SenderInformation from '@/components/order/SenderInformation.vue'

export default defineComponent({
  name: 'SendersView',
  components: {
    SenderInformation,
    Plus,
    Edit,
    Delete,
    Download,
    Warning
  },
  setup() {
    const store = useStore()
    const loading = ref(false)
    const showCreateDialog = ref(false)
    const showDeleteDialog = ref(false)
    const editingSender = ref<Sender | null>(null)
    const senderToDelete = ref<Sender | null>(null)

    const senders = computed(() => store.state.senders)

    const senderTypeLabels = {
      [SenderTypeEnum.OOO]: 'Юридическое лицо',
      [SenderTypeEnum.IP]: 'Индивидуальный предприниматель',
      [SenderTypeEnum.SELF_EMPLOYED]: 'Самозанятый'
    } as const

    const loadSenders = async () => {
      loading.value = true
      try {
        await store.dispatch('fetchSenders')
      } catch (error) {
        ElMessage.error('Не удалось загрузить список отправителей')
      } finally {
        loading.value = false
      }
    }

    const handleEdit = (sender: Sender) => {
      editingSender.value = sender
      showCreateDialog.value = true
    }

    const handleDelete = (sender: Sender) => {
      senderToDelete.value = sender
      showDeleteDialog.value = true
    }

    const handleSave = async (sender: Sender) => {
      loading.value = true
      try {
        if (editingSender.value) {
          await store.dispatch('updateSender', sender)
          ElMessage.success('Отправитель успешно обновлен')
        } else {
          await store.dispatch('createSender', sender)
          ElMessage.success('Отправитель успешно создан')
        }
        showCreateDialog.value = false
        editingSender.value = null
      } catch (error) {
        ElMessage.error('Произошла ошибка при сохранении отправителя')
      } finally {
        loading.value = false
      }
    }

    const confirmDelete = async () => {
      if (!senderToDelete.value?.id) return

      loading.value = true
      try {
        await store.dispatch('deleteSender', senderToDelete.value.id)
        ElMessage.success('Отправитель успешно удален')
        showDeleteDialog.value = false
        senderToDelete.value = null
      } catch (error) {
        ElMessage.error('Не удалось удалить отправителя')
      } finally {
        loading.value = false
      }
    }

    const handleDownload = async (sender: Sender) => {
      try {
        const response = await fetch(`/api/senders/${sender.id}/download-agreement`)
        if (!response.ok) {
          throw new Error('Не удалось скачать договор')
        }
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `agreement_${sender.name}.docx`
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      } catch (error) {
        ElMessage.error('Не удалось скачать договор')
      }
    }

    onMounted(() => {
      loadSenders()
    })

    return {
      loading,
      senders,
      showCreateDialog,
      showDeleteDialog,
      editingSender,
      senderToDelete,
      senderTypeLabels,
      handleEdit,
      handleDelete,
      handleSave,
      confirmDelete,
      handleDownload
    }
  }
})
</script>

<style scoped>
.senders-view {
  padding: 20px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header h2 {
  margin: 0;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.delete-confirmation {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 20px 0;
}

.warning-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.warning-text {
  color: #F56C6C;
  margin-top: 8px;
}
</style> 